import { ShaderMaterial, Vector2 } from "three";

export function createVideoChromaKeyMaterial(texture, keyColor) {
  const videoResolution = new Vector2();
  const updateVideoResolution = () => {
    const image = texture.image;
    videoResolution.set(
      Math.max(1, image?.videoWidth || image?.width || 1),
      Math.max(1, image?.videoHeight || image?.height || 1)
    );
  };
  updateVideoResolution();

  const material = new ShaderMaterial({
    uniforms: {
      videoTexture: { value: texture },
      keyColor: { value: keyColor.slice() },
      similarity: { value: 0.2 },
      smoothness: { value: 0.08 },
      spill: { value: 0.35 },
      videoResolution: { value: videoResolution },
      edgeContraction: { value: 0.5 },
      colorCleanup: { value: 0.25 }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D videoTexture;
      uniform vec3 keyColor;
      uniform float similarity;
      uniform float smoothness;
      uniform float spill;
      uniform vec2 videoResolution;
      uniform float edgeContraction;
      uniform float colorCleanup;
      varying vec2 vUv;

      vec2 chroma(vec3 color) {
        return vec2(
          dot(color, vec3(-0.168736, -0.331264, 0.5)),
          dot(color, vec3(0.5, -0.418688, -0.081312))
        );
      }

      float distanceFromKey(vec3 color) {
        float keySaturation = max(keyColor.r, max(keyColor.g, keyColor.b))
          - min(keyColor.r, min(keyColor.g, keyColor.b));
        // Neutral keys need brightness information to distinguish gray foregrounds.
        return keySaturation > 0.1
          ? distance(chroma(color), chroma(keyColor))
          : distance(color, keyColor);
      }

      float sampledDistance(vec2 uv) {
        return distanceFromKey(texture2D(videoTexture, uv).rgb);
      }

      void main() {
        vec4 videoColor = texture2D(videoTexture, vUv);
        float colorDistance = distanceFromKey(videoColor.rgb);

        // Expand the keyed area by half a source pixel, independent of display size.
        vec2 offset = edgeContraction / videoResolution;
        colorDistance = min(colorDistance, sampledDistance(vUv + vec2(offset.x, 0.0)));
        colorDistance = min(colorDistance, sampledDistance(vUv - vec2(offset.x, 0.0)));
        colorDistance = min(colorDistance, sampledDistance(vUv + vec2(0.0, offset.y)));
        colorDistance = min(colorDistance, sampledDistance(vUv - vec2(0.0, offset.y)));
        float alpha = smoothstep(similarity, similarity + smoothness, colorDistance);

        // Recover some foreground color only at partially keyed edges.
        vec3 recovered = clamp(
          (videoColor.rgb - (1.0 - alpha) * keyColor) / max(alpha, 0.2), 0.0, 1.0
        );
        float edgeWeight = (1.0 - smoothstep(0.65, 1.0, alpha)) * colorCleanup;
        videoColor.rgb = mix(videoColor.rgb, recovered, edgeWeight);

        // Remove excess green near the keyed edge without desaturating the subject.
        // Other key colors must not trigger green spill removal.
        float greenKey = step(max(keyColor.r, keyColor.b) + 0.05, keyColor.g);
        float spillMask = smoothstep(similarity, similarity + spill, colorDistance);
        float greenExcess = max(videoColor.g - max(videoColor.r, videoColor.b), 0.0);
        videoColor.g -= greenExcess * (1.0 - spillMask) * greenKey;

        gl_FragColor = vec4(videoColor.rgb, videoColor.a * alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
    toneMapped: false
  });

  material.onBeforeRender = updateVideoResolution;
  return material;
}
